import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Checkbox, Space, Tooltip } from 'antd';
import { Settings, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

export interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible: boolean;
  width?: number;
  render?: (v: any, row: T, index?: number) => React.ReactNode;
  excelValue?: (row: T, index?: number) => string | number | Date | undefined;
}

interface ColumnConfigState {
  order: string[];      // ordered visible keys
  hidden: string[];     // hidden keys
}

export function useColumnConfig<T>(allColumns: ColumnDef<T>[], storageKey: string) {
  const load = (): ColumnConfigState => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.order)) {
          const allKeys = new Set(allColumns.map(c => c.key));
          return {
            order: parsed.order.filter((k: string) => allKeys.has(k)),
            hidden: Array.isArray(parsed.hidden) ? parsed.hidden.filter((k: string) => allKeys.has(k)) : [],
          };
        }
        // Format cũ (array visible keys) → migrate
        if (Array.isArray(parsed)) {
          const visible = parsed;
          const hidden = allColumns.filter(c => !visible.includes(c.key)).map(c => c.key);
          return { order: visible, hidden };
        }
      }
    } catch { /* ignore */ }
    const visible = allColumns.filter(c => c.defaultVisible).map(c => c.key);
    const hidden = allColumns.filter(c => !c.defaultVisible).map(c => c.key);
    return { order: visible, hidden };
  };

  const [config, setConfig] = useState<ColumnConfigState>(load);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(config));
    } catch { /* ignore */ }
  }, [config, storageKey]);

  // Thêm cột mới (chưa biết) vào hidden để user có thể bật
  useEffect(() => {
    const known = new Set([...config.order, ...config.hidden]);
    const newKeys = allColumns.filter(c => !known.has(c.key)).map(c => c.key);
    if (newKeys.length > 0) {
      setConfig(prev => ({ ...prev, hidden: [...prev.hidden, ...newKeys] }));
    }
  }, [allColumns]);

  const toggleColumn = (key: string, checked: boolean) => {
    setConfig(prev => {
      if (checked) {
        if (prev.order.includes(key)) return prev;
        return { order: [...prev.order, key], hidden: prev.hidden.filter(k => k !== key) };
      } else {
        const newOrder = prev.order.filter(k => k !== key);
        if (newOrder.length === 0) return prev;
        return { order: newOrder, hidden: [...prev.hidden, key] };
      }
    });
  };

  const moveColumn = (key: string, direction: -1 | 1) => {
    setConfig(prev => {
      const idx = prev.order.indexOf(key);
      if (idx === -1) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.order.length) return prev;
      const newOrder = [...prev.order];
      [newOrder[idx], newOrder[newIdx]] = [newOrder[newIdx], newOrder[idx]];
      return { ...prev, order: newOrder };
    });
  };

  const resetColumns = () => {
    const visible = allColumns.filter(c => c.defaultVisible).map(c => c.key);
    const hidden = allColumns.filter(c => !c.defaultVisible).map(c => c.key);
    setConfig({ order: visible, hidden });
  };

  const colByKey = useMemo(() => {
    const m = new Map<string, ColumnDef<T>>();
    allColumns.forEach(c => m.set(c.key, c));
    return m;
  }, [allColumns]);

  const antColumns = useMemo(() => {
    return config.order
      .map(k => colByKey.get(k))
      .filter((c): c is ColumnDef<T> => !!c)
      .map(c => ({
        title: c.label,
        dataIndex: c.key,
        width: c.width,
        render: c.render,
      }));
  }, [config.order, colByKey]);

  const exportExcel = (items: T[], sheetName: string, fileName: string) => {
    const cols = config.order.map(k => colByKey.get(k)).filter((c): c is ColumnDef<T> => !!c);
    const rows = items.map((t, idx) => {
      const row: Record<string, any> = {};
      cols.forEach(c => {
        row[c.label] = c.excelValue ? c.excelValue(t, idx) : '';
      });
      return row;
    });
    if (rows.length === 0) return false;

    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = cols.map(c => ({
      wch: Math.max(c.label.length, ...rows.map(r => String(r[c.label] ?? '').length)) + 2,
    }));
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
    return true;
  };

  const ColumnConfigButton = () => (
    <Button icon={<Settings size={14} />} onClick={() => setModalOpen(true)}>
      Cột hiển thị ({config.order.length})
    </Button>
  );

  const ColumnConfigModal = () => (
    <Modal
      title="Cấu hình cột hiển thị"
      open={modalOpen}
      onCancel={() => setModalOpen(false)}
      width={560}
      footer={[
        <Button key="reset" onClick={resetColumns}>Mặc định</Button>,
        <Button key="close" type="primary" onClick={() => setModalOpen(false)}>Xong</Button>,
      ]}
    >
      <p style={{ color: '#7d8a98', fontSize: 12, marginBottom: 12 }}>
        Tích chọn cột cần hiển thị và dùng mũi tên để sắp xếp thứ tự cột.
      </p>

      <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>
        Đang hiển thị ({config.order.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
        {config.order.map((key, idx) => {
          const c = colByKey.get(key);
          if (!c) return null;
          return (
            <div key={key} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
              border: '1px solid #d5e3f2', borderRadius: 4, background: '#f0f7ff',
            }}>
              <GripVertical size={14} style={{ color: '#8a95a5' }} />
              <Checkbox
                checked
                onChange={(e) => toggleColumn(key, e.target.checked)}
              />
              <span style={{ fontSize: 13, flex: 1 }}>{c.label}</span>
              <span style={{ fontSize: 11, color: '#8a95a5' }}>#{idx + 1}</span>
              <Space.Compact>
                <Tooltip title="Lên">
                  <Button
                    size="small"
                    icon={<ArrowUp size={12} />}
                    disabled={idx === 0}
                    onClick={() => moveColumn(key, -1)}
                  />
                </Tooltip>
                <Tooltip title="Xuống">
                  <Button
                    size="small"
                    icon={<ArrowDown size={12} />}
                    disabled={idx === config.order.length - 1}
                    onClick={() => moveColumn(key, 1)}
                  />
                </Tooltip>
              </Space.Compact>
            </div>
          );
        })}
      </div>

      {config.hidden.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>
            Đã ẩn ({config.hidden.length})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {config.hidden.map(key => {
              const c = colByKey.get(key);
              if (!c) return null;
              return (
                <label key={key} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px',
                  border: '1px solid #eceef1', borderRadius: 4, cursor: 'pointer',
                  background: '#fff',
                }}>
                  <Checkbox
                    checked={false}
                    onChange={(e) => toggleColumn(key, e.target.checked)}
                  />
                  <span style={{ fontSize: 13, color: '#5b6875' }}>{c.label}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );

  return {
    antColumns,
    exportExcel,
    ColumnConfigButton,
    ColumnConfigModal,
    visibleColumns: new Set(config.order),
  };
}
